import HourglassBottomIcon from '@mui/icons-material/HourglassBottom'
import { Alert, Box, Grid, Skeleton, Stack, useMediaQuery } from '@mui/material'
import { SectionCardContainer } from '~/components/common/SectionCardContainer'
import { FC, useCallback, useMemo, useState } from 'react'
import { ResponsiveApprovedCampaignCard } from './ResponsiveApprovedCampaignCard'
import { ApprovedCampaignsTab, TabState } from './ResponsiveCampaignsTab'
import {
  CampaignListQuery,
  CampaignOrderField,
  CampaignStatus,
  RegularlyPublishedCampaignListQuery,
  useCampaignListQuery,
  useRegularlyPublishedCampaignListQuery,
} from '~/generated/graphql'
import { snackActions } from '~/lib/snackbarHelper'
import { ErrorAlert } from '~/components/common/ErrorAlert'
import { ApolloError } from '@apollo/client'
import { ResponsiveApprovedCampaignPagination } from './ResponsiveApprovedCampaignPagination'
import { createCampaignMock } from '~/mocks/createCampaignMock'
import { encodeId } from '~/lib/convertId'
import { toBurdenRate } from '~/features/campaign_input/constants/burdenRate'

// 1ページに表示するキャンペーンの数
const DISPLAY_COUNT = 15 as const

// 承認済みキャンペーンの取得条件
const approveCampaignInput = {
  input: {
    status: [CampaignStatus.Approved],
    take: DISPLAY_COUNT,
    orderBy: {
      direction: 'DESC',
      field: CampaignOrderField.ApprovedAt,
    },
  },
  campaignCountInput: {
    status: [CampaignStatus.Approved],
  },
} as const

// アーカイブ済みキャンペーンの取得条件
const archivedCampaignInput = {
  input: {
    status: [CampaignStatus.Denial, CampaignStatus.Finish],
    take: DISPLAY_COUNT,
    orderBy: {
      direction: 'DESC',
      field: CampaignOrderField.ApprovedAt,
    },
  },
  campaignCountInput: {
    status: [CampaignStatus.Denial, CampaignStatus.Finish],
  },
} as const

export const ResponsiveApprovedCampaignList = () => {
  // 承認済みキャンペーンの取得
  const {
    data: approvedData,
    loading: approvedLoading,
    error: approvedError,
    refetch: approvedRefetch,
  } = useCampaignListQuery({
    variables: approveCampaignInput,
    notifyOnNetworkStatusChange: true,
  })

  // アーカイブ済みキャンペーンの取得
  const {
    data: archivedData,
    refetch: archivedRefetch,
    loading: archivedLoading,
    error: archivedError,
  } = useCampaignListQuery({
    variables: archivedCampaignInput,
    notifyOnNetworkStatusChange: true,
  })

  // 定期公開キャンペーンの取得
  const {
    data: regularlyPublishedData,
    loading: regularlyPublishedLoading,
    refetch: regularlyPublishedRefetch,
    error: regularlyPublishedError,
  } = useRegularlyPublishedCampaignListQuery()

  const [tabState, setTabState] = useState<TabState>('running')
  const handleTabChange = useCallback((tabValue: TabState) => setTabState(tabValue), [])

  const handleApprovedPageChange = async (page: number) =>
    approvedRefetch({
      input: {
        ...approveCampaignInput.input,
        skip: (page - 1) * DISPLAY_COUNT,
      },
      campaignCountInput: approveCampaignInput.campaignCountInput,
    })

  const handleArchivedPageChange = async (page: number) =>
    archivedRefetch({
      input: {
        ...archivedCampaignInput.input,
        skip: (page - 1) * DISPLAY_COUNT,
      },
      campaignCountInput: archivedCampaignInput.campaignCountInput,
    })

  return (
    <SectionCardContainer title="公開済みキャンペーン" icon={<HourglassBottomIcon sx={{ fontSize: '20px' }} />}>
      <ApprovedCampaignsTab
        {...{
          tabState,
          handleTabChange,
          loading: approvedLoading && archivedLoading,
        }}
      >
        <Stack gap={4}>
          {tabState === 'running' && (
            <CampaignGrid
              key="running"
              tabState="running"
              data={approvedData}
              loading={approvedLoading}
              error={approvedError}
              handlePageChange={handleApprovedPageChange}
              refetchRegularlyPublishedCampaigns={regularlyPublishedRefetch}
            />
          )}
          {tabState === 'closed' && (
            <CampaignGrid
              key="closed"
              tabState="closed"
              data={archivedData}
              loading={archivedLoading}
              error={archivedError}
              handlePageChange={handleArchivedPageChange}
              refetchRegularlyPublishedCampaigns={regularlyPublishedRefetch}
            />
          )}
          {tabState === 'regularPublished' && (
            <CampaignGrid
              key="regularPublished"
              tabState="regularPublished"
              data={regularlyPublishedData}
              loading={regularlyPublishedLoading}
              error={regularlyPublishedError}
              refetchRegularlyPublishedCampaigns={regularlyPublishedRefetch}
            />
          )}
        </Stack>
      </ApprovedCampaignsTab>
    </SectionCardContainer>
  )
}

const LoadingApprovedCampaignList: FC = () => {
  return [...Array(15).keys()].map((index) => (
    <Grid
      key={index}
      size={{
        xl: 2.4,
        lg: 3,
        md: 4,
        sm: 6,
        xs: 12,
      }}
    >
      <Skeleton>
        <ResponsiveApprovedCampaignCard
          campaign={{
            ...createCampaignMock({ name: 'dummy'.repeat(10) }),
            id: encodeId('Campaign', index),
            status: 'APPROVAL_REQUEST',
            scheduleType: 'NOT_USED',
            costHandlingMethod: 'NoChargeCampaign',
            influencerBurdenRate: 0,
            offerStateCount: {
              entry: 0,
              working: 0,
              completed: 0,
            },
            coverImageUrls: [],
            campaignSnsRequirements: [],
          }}
          tabState="running"
          refetchRegularlyPublishedCampaigns={() => {}}
        />
      </Skeleton>
    </Grid>
  ))
}

const CampaignGrid: FC<
  {
    loading: boolean
    refetchRegularlyPublishedCampaigns: () => void
    error?: ApolloError
    handlePageChange?: (page: number) => void
  } & (
    | {
        tabState: 'running' | 'closed'
        data?: CampaignListQuery
      }
    | {
        tabState: 'regularPublished'
        data?: RegularlyPublishedCampaignListQuery
      }
  )
> = (props) => {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'))
  const [page, setPage] = useState(1)

  const campaignCount = useMemo(() => {
    if (!props.data) return 0

    if (props.tabState === 'regularPublished') {
      return props.data.viewer.regularlyPublishedCampaigns.length
    }

    return props.data.viewer.campaignCount
  }, [props.data, props.tabState])

  const campaigns = useMemo(() => {
    if (!props.data) return []

    if (props.tabState === 'regularPublished') {
      return props.data.viewer.regularlyPublishedCampaigns.slice((page - 1) * DISPLAY_COUNT, page * DISPLAY_COUNT)
    }

    return props.data.viewer.campaigns
  }, [props.data, props.tabState, page])

  const totalPage = useMemo(() => {
    return Math.ceil(campaignCount / DISPLAY_COUNT)
  }, [campaignCount])

  const handlePageChange = async (page: number) => {
    try {
      setPage(page)
      props.handlePageChange?.(page)
    } catch (e) {
      snackActions.error('キャンペーンの読み込みに失敗しました')
    }
  }

  const CampaignPagination: FC = () => (
    <ResponsiveApprovedCampaignPagination
      {...props}
      page={page}
      totalPage={totalPage}
      handlePageChange={handlePageChange}
    />
  )

  if (props.error) {
    return <ErrorAlert title="キャンペーン一覧の取得に失敗しました" error={props.error} />
  }

  if (props.loading || !props.data) {
    return (
      <Box sx={{ p: 2 }}>
        {isMobile && (
          <Box sx={{ mb: 2 }}>
            <CampaignPagination />
          </Box>
        )}
        <Grid container spacing={2}>
          <LoadingApprovedCampaignList />
        </Grid>
        <Box sx={{ mt: 2 }}>
          <CampaignPagination />
        </Box>
      </Box>
    )
  }

  if (campaignCount === 0) {
    return <Alert severity="info">該当するキャンペーンがありません。</Alert>
  }

  return (
    <Box sx={{ p: 2 }}>
      {isMobile && (
        <Box sx={{ mb: 2 }}>
          <CampaignPagination />
        </Box>
      )}
      <Grid container spacing={2}>
        {campaigns.map((campaign) => (
          <Grid
            key={campaign.id}
            size={{
              xl: 2.4,
              lg: 3,
              md: 4,
              sm: 6,
              xs: 12,
            }}
          >
            <ResponsiveApprovedCampaignCard
              campaign={{
                ...campaign,
                influencerBurdenRate: toBurdenRate(campaign.influencerBurdenRate),
              }}
              tabState={props.tabState}
              refetchRegularlyPublishedCampaigns={props.refetchRegularlyPublishedCampaigns}
            />
          </Grid>
        ))}
      </Grid>
      <Box sx={{ mt: 2 }}>
        <CampaignPagination />
      </Box>
    </Box>
  )
}
